import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import store from "@/store/index";
import { i18n } from "@/localization";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/",
    component: loadView("index"),
    meta: {
      beforeResolve(to: any, from: any, next: any) {
        store.dispatch("startLoadingPage");

        const promises = [];
        promises.push(store.dispatch("device/readName"));
        promises.push(store.dispatch("device/readModel"));
        promises.push(store.dispatch("group/index"));
        promises.push(store.dispatch("trunk/index"));
        promises.push(store.dispatch("poe/read"));
        promises.push(store.dispatch("poe/ports"));
        promises.push(store.dispatch("port/index"));
        promises.push(store.dispatch("port/indexRlinkxPorts"));
        promises.push(store.dispatch("feature/index"));
        promises.push(store.dispatch("power/read"));
        promises.push(store.dispatch("profile/name"));
        promises.push(store.dispatch("device/readConfigMode"));

        Promise.all(promises)
          .then(() => next())
          .finally(() => store.dispatch("stopLoadingPage"));
      },
    },
    children: [
      {
        path: "/",
        name: "status",
        component: loadView("status"),
        meta: {
          title: i18n.t("views.side.status"),
          beforeResolve(to: any, from: any, next: any) {
            store.dispatch("startLoadingPage");

            const promises = [];
            promises.push(store.dispatch("ptp/index"));
            promises.push(store.dispatch("device/readIpSettings"));

            Promise.all(promises)
              .then(() => next())
              .finally(() => store.dispatch("stopLoadingPage"));
          },
        },
      },
      {
        path: "/help",
        name: "help",
        component: loadView("help"),
        meta: {
          title: i18n.t("views.side.help"),
        },
      },
      {
        path: "/device-settings",
        name: "device-settings",
        component: loadView("device-settings"),
        meta: {
          title: i18n.t("views.side.device_settings"),
          beforeResolve(to: any, from: any, next: any) {
            store.dispatch("startLoadingPage");

            const promises = [];
            promises.push(store.dispatch("device/read"));
            promises.push(store.dispatch("device/readMdns"));
            promises.push(store.dispatch("device/readIpSettings"));
            promises.push(store.dispatch("device/readResetAllowed"));
            promises.push(store.dispatch("poe/read"));
            promises.push(store.dispatch("snmp/read"));
            promises.push(store.dispatch("security/read"));
            promises.push(store.dispatch("security/getDefaultConfig"));

            store.dispatch("device/readEInk");

            Promise.all(promises)
              .then(() => next())
              .finally(() => store.dispatch("stopLoadingPage"));
          },
        },
      },
      {
        path: "/groups-trunks",
        name: "groups-trunks",
        component: loadView("groups-trunks"),
        meta: {
          title: i18n.t("views.side.groups_trunks"),
          beforeResolve(to: any, from: any, next: any) {
            store.dispatch("startLoadingPage");

            const promises = [];
            promises.push(store.dispatch("group/index"));
            promises.push(store.dispatch("trunk/index"));
            promises.push(store.dispatch("device/readIpSettings"));
            promises.push(store.dispatch("group/readIgmpQuerierIps"));

            Promise.all(promises)
              .then(() => next())
              .finally(() => store.dispatch("stopLoadingPage"));
          },
        },
      },
      {
        path: "/port-settings",
        name: "port-settings",
        component: loadView("port-settings"),
        meta: {
          title: i18n.t("views.side.port_settings"),
          beforeResolve(to: any, from: any, next: any) {
            store.dispatch("startLoadingPage");

            const promises = [];
            promises.push(store.dispatch("poe/ports"));

            Promise.all(promises)
              .then(() => next())
              .finally(() => store.dispatch("stopLoadingPage"));
          },
        },
      },
      {
        path: "/toolbox",
        name: "toolbox",
        component: loadView("toolbox"),
        meta: {
          title: i18n.t("views.side.toolbox"),
          beforeResolve(to: any, from: any, next: any) {
            store.dispatch("startLoadingPage");

            const promises = [];
            promises.push(store.dispatch("device/read"));
            promises.push(store.dispatch("security/getDefaultConfig"));
            promises.push(store.dispatch("profile/index"));

            Promise.all(promises)
              .then(() => next())
              .finally(() => store.dispatch("stopLoadingPage"));
          },
        },
      },
      {
        path: "/eula",
        name: "eula",
        component: loadView("eula"),
        meta: {
          title: i18n.t("views.eula"),
        },
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeResolve(async (routeTo: any, routeFrom: any, next: any) => {
  try {
    await store.dispatch("startLoadingPage");

    const eula = await store.dispatch("device/readEula");
    if (routeTo.name !== "eula" && !eula.accepted) {
      next({ name: "eula" });
      return;
    }
    if (routeFrom.meta && routeFrom.meta.leaveCallback) {
      await routeFrom.meta.leaveCallback(routeTo, routeFrom);
    }

    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.title) {
          document.title = store.getters["device/name"] + " | " + route.meta.title;
        }
        if (route.meta && route.meta.group) {
          document.querySelector("body")?.classList.add(route.meta.group);
        }

        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args: any) => {
            if (args.length) {
              store.dispatch("stopLoadingPage");
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve({ success: true });
            }
          });
        } else {
          resolve({ success: true });
        }
      });
    }
  } catch (error) {
    return;
  }
  await store.dispatch("stopLoadingPage");
  next();
});

function loadView(view: string) {
  return () => import(`@/views/${view}.vue`);
}

export default router;
